import dayjs from 'dayjs';
import { OrderV2 } from '@kotipizzagroup/kotipizza-orders-api-client';

export const notPaidTimeStamp = '0001-01-01T00:00:00+00:00';
const codPaymentMethod = 'COD';
const tenMinutesInMilliseconds = 600000; // 10 * 60 * 1000

export enum OrderPaymentState {
  Paid = 'Paid',
  PaidInRestaurant = 'PaidInRestaurant',
  Unpaid = 'Unpaid',
  PaymentNotCompleted = 'PaymentNotCompleted',
  PaymentReceivedTooLate = 'PaymentReceivedTooLate',
  PaymentCancelled = 'PaymentCancelled',
}

export const getOrderPaymentState = (order: OrderV2): OrderPaymentState => {
  if (order.orderPaymentCancellationTimestamp) {
    return OrderPaymentState.PaymentCancelled;
  }

  // Order NOT paid
  if (!order.orderPaid || order.orderPaid === notPaidTimeStamp) {
    const placedMillisecondsAgo = Math.abs(dayjs(order.orderPlaced).diff());

    // We should've gotten the payment within 10 minutes, if not -> payment wasn't completed in time
    if (placedMillisecondsAgo > tenMinutesInMilliseconds) {
      return OrderPaymentState.PaymentNotCompleted;
    }

    return OrderPaymentState.Unpaid;
  }
  // Order paid
  else {
    // COD payments are always paid in restaurant (both COD & OnSite have the COD payment method)
    if (order.paymentMethod && order.paymentMethod.toUpperCase() === codPaymentMethod) {
      return OrderPaymentState.PaidInRestaurant;
    }

    // Check the difference between placed and paid timestamps so that we can detect orders where the payment
    // was received a long time after making the order. PaidOrdersPublisher polls for paid unhandled orders only for
    // 6 hours in the past, so when the payment takes longer than that to arrive, it means that the orders aren't
    // send to the POS at all.
    const orderPlacedTimeStamp = dayjs(order.orderPlaced);
    const orderPaidTimeStamp = dayjs(order.orderPaid);

    const hoursBetweenPlacedAndPaid = Math.abs(orderPlacedTimeStamp.diff(orderPaidTimeStamp, 'hours'));

    // If the payment was received over 6 hours later, the order wasn't processed at all
    if (hoursBetweenPlacedAndPaid > 6) {
      return OrderPaymentState.PaymentReceivedTooLate;
    }

    return OrderPaymentState.Paid;
  }
};

/**
 * Whether the customer's money has actually arrived. Deliberately counts PaymentReceivedTooLate as
 * paid: the payment did land, it just landed too late for the POS to pick it up.
 */
export const isOrderPaid = (order: OrderV2): boolean => {
  const paymentState = getOrderPaymentState(order);

  return (
    paymentState === OrderPaymentState.Paid ||
    paymentState === OrderPaymentState.PaidInRestaurant ||
    paymentState === OrderPaymentState.PaymentReceivedTooLate
  );
};
